import { EventEmitter, inject, Injectable } from '@angular/core';
import { InstanceUpdatesManagerService } from '@cloud-twin-v2/cloud-twin-shared/workers/instance-updates-manager.service';
import { CTMapPersistencyService } from '@cloud-twin-v2/ct-map/services/ct-map-persistency.service';
import { CtMapThematicsService } from '@cloud-twin-v2/ct-map/services/ct-map-thematics.service';
import { WorkspacesService } from '@cloud-twin-v2/ct-workspaces/services/workspaces.service';
import { CtWorkspaceDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { TelemetryPointsService } from '@cloud-twin-v2/telemetry/services/telemetry-points.service';
import { AuthenticationService } from '@common-modules/shared/auth/services/authentication.service';
import { ArrayHelperService } from '@common-modules/shared/helpers/array-helper.service';
import { LocalStorageService } from '@common-modules/shared/local-storage.service';
import { GlobalsService } from '@common-modules/shared/services/globals.service';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { CtAttributesService } from './core/attributes/ct-attributes.service';
import { CTResultsService } from './core/results/ct-results.service';

@Injectable({ providedIn: 'root' })
export class SelectedWorkspaceService {
  private _selectedWorkspace$ = new BehaviorSubject<CtWorkspaceDto>(null);
  readonly selectedWorkspace$ = this._selectedWorkspace$.asObservable();
  private readonly _selectedWorkspaceKey = 'selectedWorkspaceKey';

  private readonly _ctAttributesService = inject(CtAttributesService);
  private readonly _arrayHelperService = inject(ArrayHelperService);
  private readonly _storageService = inject(LocalStorageService);
  private readonly _ctResultsService = inject(CTResultsService);
  private readonly _telemetryPointsService = inject(TelemetryPointsService);
  private readonly _instanceUpdatesManagerService = inject(InstanceUpdatesManagerService);
  private readonly _ctMapThematicsService = inject(CtMapThematicsService);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _workspaceService = inject(WorkspacesService);
  private readonly _ctMapPersistencyService = inject(CTMapPersistencyService);
  private readonly _globalsService = inject(GlobalsService);
  projectIsCt = new EventEmitter<boolean>(false);

  init(): void {
    const workspaceIdStored = this._storageService.get(this._selectedWorkspaceKey);
    if (workspaceIdStored) {
      const selectedWorkspaceId = JSON.parse(workspaceIdStored);
      if (selectedWorkspaceId) {
        this.getWorkspaceById(selectedWorkspaceId);
      }
    }
  }

  setWorkspace(data: CtWorkspaceDto | null): void {
    const previous = this._selectedWorkspace$.value;

    if (previous?.workspaceId === data?.workspaceId) return;

    if (previous) {
      this.unregisterSyncUpdates(previous);
    }

    this._selectedWorkspace$.next(data);

    if (data) {
      this._storageService.addOrUpdate(
        this._selectedWorkspaceKey,
        JSON.stringify(data.workspaceId)
      );
      this.registerSyncUpdates(data);
      this.loadGlobalDependencies(data);
    } else {
      this._storageService.remove(this._selectedWorkspaceKey);
    }
  }

  private getWorkspaceById(workspaceId: string): void {
    this._workspaceService.getWorkspaceById(workspaceId).subscribe((workspace) => {
      this.setWorkspace(workspace);
    });
  }

  private registerSyncUpdates(workspace: CtWorkspaceDto): void {
    if (workspace) {
      this._instanceUpdatesManagerService.registerWorkspacesFromSettings(
        workspace.workspaceConnectSettings
      );
    }
  }

  private unregisterSyncUpdates(workspace: CtWorkspaceDto): void {
    if (workspace) {
      this._instanceUpdatesManagerService.unregisterWorkspacesFromSettings(
        workspace.workspaceConnectSettings
      );
    }
  }

  get isCurrentWorkspaceOwner$(): Observable<boolean> {
    return combineLatest([this.selectedWorkspace$, this._authenticationService.userCode$]).pipe(
      map(([workspace, userCode]) => workspace?.ownerCode === userCode)
    );
  }

  get isCurrentWorkspaceOwnerOrSuperUser$(): Observable<boolean> {
    return combineLatest([
      this.selectedWorkspace$,
      this._authenticationService.userCode$,
      this._globalsService.getCurrentUser(),
    ]).pipe(
      map(([workspace, userCode, user]) => user?.isSuperUser || workspace?.ownerCode === userCode)
    );
  }

  private loadGlobalDependencies(workspace: CtWorkspaceDto): void {
    const scenarioIds = this.getCurrentScenarioIds();
    this._ctAttributesService.load(scenarioIds).subscribe();
    this.loadResults(scenarioIds);
    
    this._telemetryPointsService.load(scenarioIds).subscribe();
    this._ctMapThematicsService.setCurrentWorkspace(workspace.workspaceId);
    this._ctMapPersistencyService.init(workspace);
  }
  
  private loadResults(scenarioIds: string[]) {
    this._ctAttributesService.getDateRange(scenarioIds).subscribe(dateRange => {
      const loadDates = this._ctAttributesService.getResultLoadDateRange(dateRange.startDate, dateRange);
      this._ctResultsService.load(scenarioIds, loadDates.startDate, loadDates.endDate).subscribe();
    });
  }

  private getCurrentScenarioIds(): string[] {
    let scenarioIds = this._selectedWorkspace$.value?.workspaceConnectSettings?.map(
      (item) => item.scenarioId
    );
    if (!scenarioIds) {
      return [];
    }
    scenarioIds = this._arrayHelperService.onlyUnique(scenarioIds);
    return scenarioIds;
  }

  get selectedWorkspace() {
    return this._selectedWorkspace$.value;
  }
}
